interface Props {
  large?: boolean;
  text: string;
  sale?: boolean;
  /** Overrides the ribbon fill class — used by theme-coloured product tag badges. */
  fillClassName?: string;
  /** Overrides the label fill class — used by theme-coloured product tag badges. */
  textClassName?: string;
}
const SvgBadge: React.FC<Props> = ({
  large,
  text,
  sale,
  fillClassName,
  textClassName,
}) => {
  const discount = text?.startsWith("-") || sale;
  const height = large ? 21 : 18;
  const notch = large ? 5 : 4;
  const fill = fillClassName ?? (discount ? "fill-badge-sale" : "fill-badge-new");
  const labelClass = textClassName ?? "";
  const tone = fillClassName ? "tag" : discount ? "sale" : "new";

  return (
    <span
      className={`product-badge product-badge--${tone}${
        large ? " product-badge--large" : ""
      }`}
      // Declared inline as well as in SCSS, deliberately. This badge has now been broken
      // twice on device by the stylesheet lagging behind the markup, and the failure mode
      // is invisible-or-unreadable rather than merely unstyled. Everything here is layout
      // and colour that the component fully owns, and the tenant theme is untouched — the
      // values are still the same custom properties, just declared on the element instead
      // of in a rule that has to arrive separately. The SCSS is kept as the canonical
      // source; these are the same declarations, so nothing diverges.
      style={{
        display: "inline-flex",
        alignItems: "stretch",
        height,
        marginTop: -8,
      }}
    >
      <span
        className={`product-badge__label ${fillClassName || ""} ${labelClass}`.trim()}
        style={{
          display: "flex",
          alignItems: "center",
          paddingInline: "6px 2px",
          fontSize: 11,
          fontWeight: 700,
          lineHeight: 1,
          whiteSpace: "nowrap",
          ...(fillClassName
            ? {}
            : {
                background: `var(--${tone}-badge-bg-color, ${
                  discount ? "#ed1414" : "#f9a000"
                })`,
              }),
          ...(textClassName
            ? {}
            : {
                color: `var(--${tone}-badge-color, #ffffff)`,
              }),
        }}
      >
        {text}
      </span>
      {/* Two triangles meeting at the mid-height tip — the V bitten out of the trailing
          edge. A shape, never text, so the paint bug above cannot reach it; and if it
          ever did fail, the cost is a 4px notch rather than the whole label. */}
      <svg
        className="product-badge__tail"
        style={{ display: "block", flex: "none" }}
        width={notch}
        height={height}
        viewBox={`0 0 ${notch} ${height}`}
        aria-hidden="true"
        focusable="false"
      >
        <path
          d={`M0 0L${notch} 0L0 ${height / 2}L${notch} ${height}L0 ${height}Z`}
          className={fill}
        />
      </svg>
    </span>
  );
};

export default SvgBadge;
